<template>
  <div
    class="wrapper flex flex-column m:flex-row flex-gap m:flex-stretch relative padding-m l:padding-remove l:padding-m-inline-end"
    :class="computedWrapperClass">
    <div
      class="relative preview background-cover flex-middle flex-center cursor-pointer"
      @click.prevent.stop="openMediaManager">
      <div
        v-show="isMediaSelected"
        class="media-preview absolute-cover"
        :style="`background-image: url('${value}');`"></div>
      <icon v-show="!isMediaSelected" icon="image" size="3" theme="gray"></icon>
    </div>

    <div class="actions flex-column flex-between gap-m padding-s-top">
      <div class="flex gap-m" v-if="isMediaSelected">
        <form-button
          v-if="isMediaSelected"
          text="core.form.mediaUrl.changeCta"
          theme="gray-5"
          class="width-expand"
          icon="refresh"
          @click.prevent.stop="openMediaManager"></form-button>
        <form-button
          v-if="isMediaSelected"
          text="core.form.mediaUrl.clearCta"
          theme="danger"
          icon="x"
          class="width-expand"
          @click.prevent.stop="value = null"></form-button>
        <form-button
          v-if="isMediaSelected"
          :text="false"
          theme="inverse"
          icon="info"
          class="square"
          :slim="true"
          @click.prevent.stop="showImageInfo"></form-button>
      </div>

      <form-button
        v-if="!isMediaSelected"
        text="core.form.mediaUrl.selectCta"
        theme="success"
        icon="check"
        @click.prevent.stop="openMediaManager"></form-button>
      <div class="input-wrapper">
        <form-input
          v-bind="mediaUrlInputAttrs"
          ref="inputElement"
          v-model="value"
          theme-style="minimal"
          icon="link"
          style="max-width: 100%"
          :type="'text'"
          :wrapper-padding="false"
          placeholder="media.formElements.mediaUrlInputPlaceholder"
          class="url-input field__element padding-s-top"
          :label="false"
          @focus="isFocused = true"
          @blur="isFocused = false" />
      </div>
    </div>
  </div>

  <modal
    :title="false"
    :show-footer="false"
    :show="isSelectingMedia"
    @modal:closed="isSelectingMedia = false"
    :full="true">
    <media-manager
      v-bind="mediaManagerProps"
      :media-select="true"
      @media:item-selected="setSelectedMedia"></media-manager>
  </modal>
  <modal
    @modal:closed="isShowingItem = false"
    :show="isShowingItem"
    title="core.form.mediaUrl.imageInfo.modalTitle">
    <div>
      <table
        aria-describedby="image info details"
        class="table table--middle table--inline-start table--truncate table--expand table table--highlight">
        <thead>
          <tr>
            <th>{{tt('core.form.mediaUrl.imageInfo.fieldLabel')}}</th>
            <th>{{tt('core.form.mediaUrl.imageInfo.valueLabel')}}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.url") }}</td>
            <td>
              <div class="flex-middle gap-m">
                <span
                  style="max-width: 200px"
                  class="block text-overflow-ellipsis"
                  >{{ item.url }}</span
                >
                <form-button
                  @click="copyToClipboard(item.url)"
                  theme="inverse"
                  size="s"
                  icon="clipboard"
                  :text="false"
                  :slim="true"></form-button>
              </div>
            </td>
          </tr>

          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.thumbUrl") }}</td>
            <td><div class="flex-middle gap-m">
                <span
                  style="max-width: 200px"
                  class="block text-overflow-ellipsis"
                >{{ item.thumbUrl }}</span
                >
              <form-button
                @click="copyToClipboard(item.thumbUrl)"
                theme="inverse"
                size="s"
                icon="clipboard"
                :text="false"
                :slim="true"></form-button>
            </div></td>
          </tr>
          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.folder") }}</td>
            <td>{{ item.folder }}</td>
          </tr>
          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.createdAt") }}</td>
            <td>
              <span class="force-ltr">{{
                translateDate(item.createdAt, "dateWithTime")
              }}</span>
            </td>
          </tr>

          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.handle") }}</td>
            <td>{{ item.handle }}</td>
          </tr>

          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.handler") }}</td>
            <td>{{ item.handler }}</td>
          </tr>

          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.id") }}</td>
            <td>{{ item.id }}</td>
          </tr>

          <tr>
            <td>{{ tu("core.form.mediaUrl.imageInfo.mimeType") }}</td>
            <td>{{ item.mimeType }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </modal>
</template>

<script>
import SimpleInputMixin from "@/client/extensions/mixins/baseComponents/form/controls/input/simpleInput.js";
import asyncOperations from "@/client/extensions/composition/asyncOperations";
export default {
  mixins: [SimpleInputMixin],
  props: {
    class: {
      default: "",
    },
    placeholder: {
      default: "test",
    },
    mediaManagerProps: {
      type: Object,
      default: {},
    },
    showImageInfoButton: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);

    return {
      asyncOps,
      asyncOpsReady,
      asyncStatus,
    };
  },
  data() {
    return {
      isSelectingMedia: false,
      item: null,
      isShowingItem: false,
    };
  },
  computed: {
    computedWrapperClass() {
      let localClass = this.class;
      let exploded = localClass.split(" ");
      let result = exploded.filter((className) => {
        return className !== "field__element";
      });

      return result.join(" ");
    },
    isMediaSelected() {
      return this.value && this.value !== "";
    },
    mediaUrlInputAttrs() {
      let result = { ...this.inputAttrs };
      delete result.legend;
      return result;
    },
  },
  methods: {
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$s.ui.notification(
            "core.form.mediaUrl.imageInfo.copySuccessful",
            "success"
          );
        })
        .catch((err) => {});
    },
    openMediaManager() {
      this.isSelectingMedia = false;
      this.isSelectingMedia = true;
    },
    setSelectedMedia(data) {
      this.isSelectingMedia = false;
      this.value = data.item.url;
    },
    async showImageInfo() {
      let showImageInfoCallback = async () => {
        if (typeof this.value !== "string") {
          this.$s.ui.notification("core.form.mediaUrl.errorGettingInfo", "error");
          return false;
        }

        let val = this.value.slice(0, 500).split("?")[0];
        const regex =
                /\/api\/files\/[0-9a-fA-F-]+\/([0-9a-fA-F-]+\.[a-zA-Z0-9]+)(?=\?|\/|&|$)/;

        const match = val.match(regex);

        let uuid;
        if (Array.isArray(match) || !match[1]) {
          uuid = match[1];
        } else {
          this.$s.ui.notification("core.form.mediaUrl.errorGettingInfo", "error");
          return false;
        }

        let requestResult = await this.asyncOps.asyncCall("media-manager", {
          pagination: {
            start: 0,
            limit: 1,
          },
          filters: {
            handle: uuid,
          },
        });

        if (requestResult.hasError) {
          this.$s.ui.notification("core.form.mediaUrl.errorGettingInfo", "error");
          return false;
        }

        if (requestResult.data.items.length < 1) {
          this.$s.ui.notification("core.form.mediaUrl.errorGettingInfo", "error");
          return false;
        }

        let item = requestResult.data.items[0];
        this.item = item;
        this.isShowingItem = true;
        return true;
      };
      this.performWithUi(showImageInfoCallback, {
        successText: false,
        errorText: "core.form.mediaUrl.errorGettingInfo",
      })
    },
  },
};
</script>

<style scoped lang="scss">
.preview {
  --image-url-preview-width: 150px;
  width: var(--image-url-preview-width);
  flex: 0 0 var(--image-url-preview-width);
  aspect-ratio: 16/9;
  border-inline-end: 1px solid var(--c-gray-2);
  background-color: var(--c-gray-1);
  box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.1) inset;
  border-radius: var(--border-radius-sharp) 0 0 var(--border-radius-sharp);
  @media screen and (max-width: 767px) {
    width: 150px;
    flex: 0 0 150px;
    height: calc(150px * 9 / 16);
    margin-inline: auto;
  }
}

.media-preview {
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;
}

.url-input {
  padding-inline: var(--margin-s);
}
</style>
